import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radar, CheckCircle2, AlertTriangle, XCircle, Slash, HelpCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { ScannerSignal, ScannerStatus } from '@/api/types';
import { cn } from '@/lib/cn';
import { ago, timeOnly } from '@/lib/format';

const STATUS_CFG: Record<string, { tone: string; bg: string; icon: typeof CheckCircle2; label: string }> = {
  ok:       { tone: 'text-kam-green', bg: 'bg-kam-green-soft border-kam-green/25', icon: CheckCircle2,  label: 'OK' },
  no_data:  { tone: 'text-text-faint',bg: 'bg-surface-2 border-[color:var(--border)]', icon: Slash,     label: 'No data' },
  disabled: { tone: 'text-text-faint',bg: 'bg-surface-2 border-[color:var(--border)]', icon: Slash,     label: 'Disabled' },
  failed:   { tone: 'text-kam-red',   bg: 'bg-kam-red-soft border-kam-red/25',     icon: XCircle,        label: 'Failed' },
  missing:  { tone: 'text-kam-amber', bg: 'bg-kam-amber-soft border-kam-amber/30', icon: AlertTriangle,  label: 'Missing' },
};

const CONFIDENCE_CFG: Record<string, { tone: string; label: string }> = {
  REGULATOR_CONFIRMED: { tone: 'text-kam-green border-kam-green/30 bg-kam-green-soft', label: 'KRX' },
  DISCLOSED:           { tone: 'text-kam-violet border-kam-violet/30 bg-kam-violet-soft', label: 'Disclosed' },
  DERIVED:             { tone: 'text-text-dim border-[color:var(--border)] bg-surface-2', label: 'Derived' },
};

const todayIso = () => new Date().toISOString().slice(0, 10);

export function Scanners() {
  const [date, setDate] = useState<string>(todayIso());

  const { data, isLoading, isError, dataUpdatedAt } = useQuery({
    queryKey: ['scannerDiscovery', date],
    queryFn: () => dashboardApi.scannerDiscovery(date),
    refetchInterval: 30_000,
  });

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Radar size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Scanner Discoveries</h1>
          <div className="text-xs text-text-faint">
            {isLoading ? 'loading…'
              : isError ? 'unable to reach /api/scanners/discovery'
              : data
                ? `${data.signals.length} actionable signals · refreshed ${ago(new Date(dataUpdatedAt).toISOString())}`
                : ''}
          </div>
        </div>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="bg-surface-2 border border-[color:var(--border)] rounded-md px-2 py-1 text-sm text-text font-mono"
        />
      </div>

      {data && (
        <div className="space-y-6 max-w-5xl">
          {/* Scanner status row */}
          <section>
            <h2 className="text-sm font-semibold mb-2 text-text-dim">Scanner Status</h2>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {data.scanners.map((s) => <StatusCard key={s.scannerName} s={s} />)}
            </div>
          </section>

          {/* Actionable signal feed */}
          <section>
            <h2 className="text-sm font-semibold mb-2 text-text-dim">
              Actionable Signals
            </h2>
            {data.signals.length === 0
              ? <div className="card card-tight text-xs text-text-faint italic">
                  No actionable signals for this date. Scanners with status="ok" ran cleanly and found nothing — that's the expected steady state on quiet days.
                </div>
              : <div className="card card-tight overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-text-faint text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left font-medium py-1.5 px-2">Time</th>
                        <th className="text-left font-medium py-1.5 px-2">Scanner</th>
                        <th className="text-left font-medium py-1.5 px-2">Code</th>
                        <th className="text-left font-medium py-1.5 px-2">Signal</th>
                        <th className="text-right font-medium py-1.5 px-2">Magnitude</th>
                        <th className="text-left font-medium py-1.5 px-2">Confidence</th>
                        <th className="text-left font-medium py-1.5 px-2">Context</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.signals.map((sig, i) => <SignalRow key={`${sig.scannerName}-${sig.instrumentCode}-${i}`} sig={sig} />)}
                    </tbody>
                  </table>
                </div>}
          </section>
        </div>
      )}
    </div>
  );
}

function StatusCard({ s }: { s: ScannerStatus }) {
  const cfg = STATUS_CFG[s.status] ?? { tone: 'text-text-dim', bg: 'bg-surface-2 border-[color:var(--border)]', icon: HelpCircle, label: s.status };
  const Icon = cfg.icon;
  return (
    <div className={cn('card card-tight border', cfg.bg)}>
      <div className="flex items-start gap-2">
        <Icon size={16} className={cn('mt-0.5 flex-none', cfg.tone)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="text-xs font-semibold font-mono">{s.scannerName}</span>
            <span className={cn('text-[10px] font-semibold uppercase', cfg.tone)}>{cfg.label}</span>
          </div>
          <div className="text-xs text-text-dim">
            {s.actionableCount > 0
              ? <><span className="text-text font-semibold">{s.actionableCount}</span> signals</>
              : <span className="text-text-faint">No signals</span>}
            {s.lastRunAt && <span className="text-text-faint"> · {timeOnly(s.lastRunAt)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SignalRow({ sig }: { sig: ScannerSignal }) {
  const conf = CONFIDENCE_CFG[sig.confidence] ?? CONFIDENCE_CFG.DERIVED;
  return (
    <tr className="border-t border-[color:var(--border)] align-top">
      <td className="py-1.5 px-2 text-xs text-text-faint font-mono whitespace-nowrap">{timeOnly(sig.computedAt)}</td>
      <td className="py-1.5 px-2 text-xs font-mono whitespace-nowrap">{sig.scannerName}</td>
      <td className="py-1.5 px-2 font-mono text-xs whitespace-nowrap">{sig.instrumentCode}</td>
      <td className="py-1.5 px-2 text-xs whitespace-nowrap">{sig.signalType}</td>
      <td className="py-1.5 px-2 text-right tabular-nums font-mono text-xs">
        {sig.magnitude != null ? sig.magnitude.toFixed(2) : '—'}
      </td>
      <td className="py-1.5 px-2">
        <span className={cn(
          'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[10px] font-semibold border whitespace-nowrap',
          conf.tone,
        )}>
          {conf.label}
        </span>
      </td>
      <td className="py-1.5 px-2 text-xs text-text-dim max-w-[320px] truncate" title={sig.context}>
        {sig.context}
      </td>
    </tr>
  );
}
